<form [formGroup]="form" (ngSubmit)="onSubmit()">

  <p-divider align="left">
    <div class="inline-flex align-items-center font-bold">Script Source</div>
  </p-divider>

  <div class="field grid">
    <div class="col-12">
      <p-selectButton
        [options]="ScriptSourceModeOptions"
        [(ngModel)]="sourceMode"
        [ngModelOptions]="{ standalone: true }"
      >
        <ng-template let-item pTemplate="item">
          <i class="pi {{ item.icon }}" [pTooltip]="item.label" tooltipPosition="bottom"></i>
        </ng-template>
      </p-selectButton>
    </div>
  </div>

  <!-- ── How an ImageJ macro talks to JIT ──────────────────────────────────── -->
  <details class="qs-help" *ngIf="isImageJ">
    <summary>How an ImageJ macro works in JIT</summary>
    <ul>
      <li>The image is already open - don't call <code>open()</code>. Run once per image
        (once for each image in a submitted folder).</li>
      <li>Variables: <code>imagePath</code>, <code>imageName</code>, <code>outputDir</code> (ends
        with a slash), <code>workDir</code>, <code>jitAnnotationCount</code>,
        <code>jitAnnotationNames</code>.</li>
      <li><code>jitParam("key", "fallback")</code> reads a parameter below, as text;
        <code>jitSelectAnnotation(i)</code> makes region <code>i</code> (0-based) the selection;
        <code>jitFail("message")</code> fails the run (<code>exit()</code> does not).</li>
      <li><b>Results are the files the macro saves into <code>outputDir</code></b>, e.g.
        <code>saveAs("PNG", outputDir + "mask.png")</code>. Anything saved elsewhere is not kept.</li>
      <li>Macros run headless with no network. ImageJ commands work, but windows (the ROI
        Manager, dialogs) do not and fail the run. Only allow-listed <code>run("...")</code>
        commands pass review; <code>exec</code>, <code>eval</code> and <code>call</code> never do.</li>
      <li>Very large whole-slide images are opened at full resolution and can run out of memory.</li>
    </ul>
    <a href="https://github.com/TheJacksonLaboratory/jit-parent/blob/main/doc/ImageJ-Macro-Guide.md"
       target="_blank" rel="noopener">Full guide</a>
  </details>

  <!-- ── Use a saved script ────────────────────────────────────────────────── -->
  <div class="field grid" *ngIf="sourceMode === 'saved'">
    <label class="col-12 mb-2 md:col-3 md:mb-0 text-black-alpha-90">Saved Script</label>
    <div class="col-12 md:col-9">
      <p-dropdown
        [options]="savedScripts"
        [(ngModel)]="savedScriptId"
        [ngModelOptions]="{ standalone: true }"
        optionLabel="name"
        optionValue="id"
        [filter]="true"
        filterBy="name,description"
        placeholder="Select a saved script…"
        [showClear]="true"
        [loading]="loadingSavedScripts"
      >
        <ng-template let-script pTemplate="item">
          <div>
            <div>{{ script.name }}</div>
            <small class="text-color-secondary" *ngIf="script.description">{{ script.description }}</small>
          </div>
        </ng-template>
      </p-dropdown>
      <small *ngIf="savedScriptsError" class="block mt-1 text-red-500">
        Could not load your saved scripts.
        <button pButton type="button" label="Retry" icon="pi pi-refresh"
                class="p-button-text p-button-sm" (click)="loadSavedScripts()"></button>
      </small>
      <small
        *ngIf="!loadingSavedScripts && !savedScriptsError && savedScripts.length === 0"
        class="block mt-1 text-color-secondary"
      >
        No saved scripts visible to you yet - approve and save a script from one of the other
        modes first.
      </small>
    </div>
  </div>

  <!-- ── Upload / write a script ───────────────────────────────────────────── -->
  <div class="field grid" *ngIf="sourceMode === 'scriptBody'">
    <label class="col-12 mb-2 md:col-3 md:mb-0 text-black-alpha-90">
      Script Body
      <i class="pi pi-info-circle px-1"
         [pTooltip]="scriptUploadHint"
         tooltipPosition="top"
      ></i>
    </label>
    <div class="col-12 md:col-9">
      <div class="mb-2">
        <button pButton type="button" label="Upload from my computer" icon="pi pi-upload"
                class="p-button-outlined p-button-sm" (click)="scriptFileInput.click()"></button>
        <input #scriptFileInput type="file" [accept]="scriptFileAccept" style="display:none"
               (change)="onUploadScript($event)"/>
        <span *ngIf="uploadedFileName" class="ml-2 text-color-secondary">{{ uploadedFileName }}</span>
      </div>
      <textarea
        pInputTextarea
        [(ngModel)]="scriptBody"
        [ngModelOptions]="{ standalone: true }"
        rows="12"
        class="w-full"
        style="font-family: monospace; font-size: 0.85rem;"
        [placeholder]="scriptPlaceholder"
      ></textarea>
    </div>
  </div>

  <!-- ── Choose from storage ──────────────────────────────────────────────── -->
  <div class="field grid" *ngIf="sourceMode === 'scriptKey'">
    <label class="col-12 mb-2 md:col-3 md:mb-0 text-black-alpha-90">Script File</label>
    <div class="col-12 md:col-9">
      <button pButton type="button" label="Choose from storage…" icon="pi pi-folder-open"
              class="p-button-outlined p-button-sm" (click)="openStoragePicker()"></button>
      <span class="ml-2 text-color-secondary">{{ scriptKeyLabel }}</span>
    </div>
  </div>

  <p-divider align="left">
    <div class="inline-flex align-items-center font-bold">Parameters</div>
  </p-divider>

  <div class="qs-parameters-section">
    <parameter-row
      *ngFor="let row of parameterRows; let i = index; trackBy: trackByIndex"
      [row]="row"
      (rowChange)="onParameterRowChange(i, $event)"
      (remove)="removeParameterRow(i)"
    ></parameter-row>

    <div *ngIf="parameterRows.length === 0" class="qs-empty-hint">
      No parameters added yet - {{ parametersHint }}.
    </div>

    <button pButton type="button" label="Add Parameter" icon="pi pi-plus"
            class="p-button-text p-button-sm p-button-secondary mt-1"
            (click)="addParameterRow()"></button>
  </div>

  <p-divider align="left">
    <div class="inline-flex align-items-center font-bold">Annotations</div>
  </p-divider>

  <p class="my-2 text-color-secondary">
    <i class="pi pi-map-marker px-1"></i>
    <ng-container *ngIf="regionCount > 0; else noRegions">
      {{ regionCount }} region(s) currently drawn will be sent to the script as annotations.
    </ng-container>
    <ng-template #noRegions>
      No regions currently drawn - the script will run with no annotations unless it doesn't need any.
    </ng-template>
  </p>

  <p-divider align="left">
    <div class="inline-flex align-items-center font-bold">Output</div>
  </p-divider>

  <div class="field grid">
    <label for="outputFolder" class="col-12 mb-2 md:col-3 md:mb-0 text-black-alpha-90">Output Folder</label>
    <div class="col-12 md:col-9">
      <span class="p-input-icon-right w-full">
        <button *ngIf="!form.get('editOutputFolder')?.value" type="button"
                class="p-link icon-hover" aria-label="Edit output folder"
                (click)="form.get('editOutputFolder')?.setValue(true)">
          <i class="pi pi-pencil"></i>
        </button>
        <input id="outputFolder" pInputText formControlName="outputFolder"
               placeholder="(defaults next to the input image)" class="w-full"
               [readonly]="!form.get('editOutputFolder')?.value"/>
      </span>
    </div>
  </div>

  <div class="field-checkbox">
    <p-checkbox
      formControlName="saveAsReusable"
      [binary]="true"
      class="my-1"
      id="saveAsReusable"
    ></p-checkbox>
    <label for="saveAsReusable">
      Save this script for reuse
      <i class="pi pi-info-circle px-2"
         pTooltip="Keeps this approved script in your script library (visible to anyone who can
           view this bucket) so it can be picked from 'Use a saved script' next time."
         tooltipPosition="top"
      ></i>
    </label>
  </div>

  <ng-container *ngIf="form.get('saveAsReusable')?.value">
    <div class="field grid">
      <label for="scriptName" class="col-12 mb-2 md:col-3 md:mb-0 text-black-alpha-90">Script Name</label>
      <div class="col-12 md:col-9">
        <input id="scriptName" pInputText formControlName="scriptName" class="w-full"/>
      </div>
    </div>
    <div class="field grid">
      <label for="scriptDescription" class="col-12 mb-2 md:col-3 md:mb-0 text-black-alpha-90">
        Description
      </label>
      <div class="col-12 md:col-9">
        <textarea id="scriptDescription" pInputTextarea formControlName="scriptDescription"
                   rows="2" class="w-full"></textarea>
      </div>
    </div>
  </ng-container>

  <div class="pt-3 pb-3 process-buttons flex align-items-center gap-3">
    <app-submit-button
      [label]="isSubmitting ? 'Scanning…' : 'Run Script'"
      [isSubmitting]="isSubmitting"
      [processName]="processName"
      [validationMessage]="validationMessage"
      icon="pi pi-play"
      styleClass="p-button-primary"
      [tooltip]="'Run ' + processName"
    ></app-submit-button>
    <button pButton type="button" label="Validate Script" icon="pi pi-shield"
            class="p-button-outlined"
            [disabled]="!hasScriptSource || validating"
            [loading]="validating"
            pTooltip="Check this script against JIT's safety review without running it."
            (click)="onValidateScript()"></button>
    <button pButton type="button" label="Create Rule" icon="pi pi-bell" class="p-button-outlined"
            [disabled]="(canCreateRule$ | async) === false || !hasScriptSource"
            pTooltip="Auto-run this on every new file in the selected folder."
            (click)="onCreateRule()"></button>
  </div>

</form>
